import React, { useEffect, useState } from "react";
import { X, MapPin, Calendar, User as UserIcon, Wrench, ClipboardList,
         Camera, MessageSquare, DollarSign, AlertTriangle, Navigation } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAdminUi } from "@/context/AdminUiContext";

function Chip({ children, tone = "slate" }) {
  const tones = {
    teal:   "bg-teal-500/15 text-teal-200 border-teal-400/30",
    amber:  "bg-amber-500/15 text-amber-200 border-amber-400/40",
    red:    "bg-red-500/15 text-red-300 border-red-400/40",
    slate:  "bg-slate-500/15 text-slate-200 border-slate-400/30",
    green:  "bg-emerald-500/15 text-emerald-200 border-emerald-400/40",
  };
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-mono text-[10px] uppercase tracking-[0.14em] ${tones[tone]}`}>{children}</span>;
}
function Section({ title, testid, children }) {
  return (
    <section data-testid={testid} className="mt-6">
      <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-teal-300 mb-2">{title}</div>
      {children}
    </section>
  );
}
function KV({ k, v, testid }) {
  if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) return null;
  return (
    <div className="flex items-start gap-3 py-1 border-b border-white/5 text-xs" data-testid={testid}>
      <div className="w-40 flex-shrink-0 text-slate-400 font-mono uppercase tracking-widest text-[10px]">{k}</div>
      <div className="text-slate-100 break-words">{Array.isArray(v) ? v.join(", ") : String(v)}</div>
    </div>
  );
}

export default function AdminBookingDetailDrawer() {
  const { bookingId, closeDrawers, openUser } = useAdminUi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!bookingId) { setData(null); return; }
    setLoading(true);
    api.get(`/admin/jobs/${bookingId}/full`)
      .then(({ data }) => setData(data))
      .catch((e) => { toast.error(e?.response?.data?.detail || "Failed to load booking"); closeDrawers(); })
      .finally(() => setLoading(false));
  }, [bookingId, closeDrawers]);

  if (!bookingId) return null;
  const j  = data?.job || {};
  const bp = j.price_breakdown || {};

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm" onClick={closeDrawers} data-testid="admin-booking-overlay" />
      <aside
        className="fixed inset-y-0 right-0 z-[60] w-full sm:w-[760px] bg-slate-950 border-l border-white/10 overflow-y-auto text-slate-100"
        data-testid="admin-booking-drawer"
        role="dialog" aria-modal="true"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-white/5 bg-slate-950/95 backdrop-blur">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-teal-300">Admin · Booking</div>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <div className="font-display font-bold text-lg font-mono truncate" data-testid="booking-drawer-id">{bookingId.slice(0, 8)}</div>
              {j.status && <Chip tone={j.status === "completed" ? "green" : j.status === "cancelled" ? "red" : "amber"}>{j.status.replace(/_/g, " ")}</Chip>}
              {j.payment_status && <Chip tone={j.payment_status === "paid" ? "teal" : "slate"}>payment · {j.payment_status}</Chip>}
              {j.booking_type && <Chip tone="slate">{j.booking_type}</Chip>}
            </div>
          </div>
          <button onClick={closeDrawers} data-testid="admin-booking-close" className="p-2 rounded-lg hover:bg-white/10 text-slate-300">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading || !data ? (
          <div className="p-8 text-slate-400">Loading booking…</div>
        ) : (
          <div className="px-6 pb-24 pt-4">
            {/* Parties */}
            <div className="grid sm:grid-cols-2 gap-3">
              <button
                type="button" data-testid="booking-open-client"
                onClick={() => data.client && openUser(data.client.id)}
                className="text-left rounded-xl border border-white/10 bg-white/[0.02] p-3 hover:border-teal-400/40 hover:bg-teal-500/[0.04] transition-colors">
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Customer</div>
                <div className="text-sm text-slate-100 mt-1">{data.client?.full_name || "—"}</div>
                <div className="text-[11px] text-slate-500">{data.client?.email}</div>
              </button>
              <button
                type="button" data-testid="booking-open-cleaner"
                onClick={() => data.cleaner && openUser(data.cleaner.id)}
                disabled={!data.cleaner}
                className="text-left rounded-xl border border-white/10 bg-white/[0.02] p-3 hover:border-teal-400/40 hover:bg-teal-500/[0.04] transition-colors disabled:opacity-60 disabled:cursor-default">
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Assigned cleaner</div>
                <div className="text-sm text-slate-100 mt-1">{data.cleaner?.full_name || "— unassigned —"}</div>
                <div className="text-[11px] text-slate-500">{data.cleaner?.email}</div>
                {data.team?.length > 0 && (
                  <div className="mt-1 text-[11px] text-teal-300 font-mono">Team of {data.team.length + 1}</div>
                )}
              </button>
            </div>

            {/* Service description */}
            <Section title="Service description" testid="booking-section-service">
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                <KV k="Service category" v={j.booking_type || j.category} testid="kv-service-category" />
                <KV k="Exact service"    v={j.service_key || j.car_service || j.pet_service || j.outdoor_service || j.convenience_service} testid="kv-exact-service" />
                <KV k="Space type"       v={j.space_type} />
                <KV k="Property type"    v={j.property_type || j.booking_type} />
                <KV k="Bedrooms"         v={j.bedrooms} testid="kv-bedrooms" />
                <KV k="Bathrooms"        v={j.bathrooms} testid="kv-bathrooms" />
                <KV k="Sqft bracket"     v={j.sqft_bracket} />
                <KV k="Deep clean"       v={j.deep_clean ? "yes" : undefined} />
                <KV k="Vehicle type"     v={j.car_vehicle_type} testid="kv-vehicle-type" />
                <KV k="Pet count"        v={j.pet_count > 0 ? j.pet_count : undefined} testid="kv-pet-count" />
                <KV k="Pet duration"     v={j.pet_duration} />
                <KV k="Outdoor size"     v={j.outdoor_size} />
                <KV k="Convenience size" v={j.convenience_size} />
                <KV k="Add-ons"          v={j.addons} testid="kv-addons" />
                <KV k="Special requests" v={j.notes || j.special_requests} testid="kv-notes" />
              </div>
            </Section>

            {/* Address + schedule */}
            <Section title="Address & schedule" testid="booking-section-address">
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                <KV k="Street"     v={j.street_address || j.address} />
                <KV k="Unit"       v={j.unit} />
                <KV k="City"       v={j.city} />
                <KV k="State"      v={j.state} />
                <KV k="Postal"     v={j.postal_code} />
                <KV k="Country"    v={j.country} />
                <KV k="Scheduled"  v={j.scheduled_at ? new Date(j.scheduled_at).toLocaleString() : "—"} testid="kv-scheduled-at" />
                <KV k="Time window" v={j.scheduled_time_window} />
              </div>
            </Section>

            {/* Money */}
            <Section title="Money" testid="booking-section-money">
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                <KV k="Original amount" v={bp.base != null ? `$${Number(bp.base).toFixed(2)}` : undefined} />
                <KV k="Add-ons"    v={bp.addons != null ? `$${Number(bp.addons).toFixed(2)}` : undefined} />
                <KV k="Discounts"  v={bp.discount ? `- $${Number(bp.discount).toFixed(2)}` : undefined} />
                <KV k="Tax / fees" v={bp.tax != null ? `$${Number(bp.tax).toFixed(2)}` : undefined} />
                <KV k="Total customer paid" v={`$${Number(j.total || 0).toFixed(2)} ${j.currency || "USD"}`} testid="kv-total" />
                <KV k="Platform fee (CLEANBAZ)" v={j.platform_fee != null ? `$${Number(j.platform_fee).toFixed(2)}` : undefined} testid="kv-platform-fee" />
                <KV k="Cleaner payout" v={j.cleaner_payout != null ? `$${Number(j.cleaner_payout).toFixed(2)}` : undefined} testid="kv-cleaner-payout" />
                <KV k="Refund amount"  v={j.refund_amount ? `$${Number(j.refund_amount).toFixed(2)}` : undefined} />
                <KV k="Refund status"  v={j.refund_status} />
              </div>
            </Section>

            {/* Cancellation */}
            {j.last_cancellation && (
              <Section title="Cancellation" testid="booking-section-cancellation">
                <div className="rounded-xl border border-red-400/30 bg-red-500/[0.06] p-4">
                  <div className="flex items-center gap-2 text-red-200 text-sm mb-2">
                    <AlertTriangle className="w-4 h-4" />
                    Cancelled {j.last_cancellation.at ? `on ${new Date(j.last_cancellation.at).toLocaleString()}` : ""}
                  </div>
                  <KV k="Cancelled by" v={j.last_cancellation.actor_email || j.last_cancellation.cleaner_email || j.cancelled_by_email} testid="kv-cancelled-by" />
                  <KV k="Actor role"   v={j.last_cancellation.actor_role} />
                  <KV k="Reason"       v={j.last_cancellation.reason} testid="kv-cancel-reason" />
                  <KV k="Notes"        v={j.last_cancellation.notes} />
                  <KV k="Was paid"     v={j.last_cancellation.was_paid ? "yes" : (j.last_cancellation.payment_status_at_cancel ? "no · was " + j.last_cancellation.payment_status_at_cancel : undefined)} />
                  <KV k="Original amount"  v={j.last_cancellation.total ? `$${Number(j.last_cancellation.total).toFixed(2)}` : undefined} />
                  <KV k="Cancellation fee" v={j.last_cancellation.cancellation_fee != null ? `$${Number(j.last_cancellation.cancellation_fee).toFixed(2)}` : (j.cancellation_fee != null ? `$${Number(j.cancellation_fee).toFixed(2)}` : undefined)} testid="kv-cancel-fee" />
                  <KV k="Refund amount"    v={j.last_cancellation.refund_amount ? `$${Number(j.last_cancellation.refund_amount).toFixed(2)}` : (j.refund_amount ? `$${Number(j.refund_amount).toFixed(2)}` : undefined)} testid="kv-refund-amount" />
                  <KV k="Within grace"     v={j.last_cancellation.within_grace === true ? "yes" : (j.last_cancellation.within_grace === false ? "no · late" : undefined)} />
                  <KV k="Hours before start" v={j.last_cancellation.hours_until_scheduled != null ? `${Number(j.last_cancellation.hours_until_scheduled).toFixed(2)} h` : undefined} />
                  <KV k="Stripe refund id" v={j.last_cancellation.stripe_refund_id || j.stripe_refund_id} testid="kv-stripe-refund-id" />
                  <KV k="Stripe refund status" v={j.last_cancellation.stripe_refund_status || j.stripe_refund_status} />
                  <KV k="Reassigned"   v={j.last_cancellation.was_reassigned ? "yes → " + (j.last_cancellation.reassigned_to_email || "cleaner") : undefined} />
                </div>
              </Section>
            )}

            {/* Live trip tracking snapshot (operational — purges on completion/cancel) */}
            {(j.trip_active || j.trip_started_at || j.last_trip_summary) && (
              <Section title="Live trip" testid="booking-section-trip">
                <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                  <KV k="Status" v={j.trip_active ? "active" : (j.trip_end_reason || "ended")} testid="kv-trip-status" />
                  <KV k="Started" v={j.trip_started_at ? new Date(j.trip_started_at).toLocaleString() : undefined} />
                  <KV k="Ended" v={j.trip_ended_at ? new Date(j.trip_ended_at).toLocaleString() : undefined} />
                  <KV k="Last update" v={j.trip_location_updated_at ? new Date(j.trip_location_updated_at).toLocaleString() : undefined} />
                  {j.cleaner_location && (
                    <>
                      <KV k="Transport" v={j.cleaner_location.transport_type} />
                      <KV k="Approx coords" v={`${j.cleaner_location.lat?.toFixed(4)}, ${j.cleaner_location.lng?.toFixed(4)}`} testid="kv-trip-coords" />
                    </>
                  )}
                  <KV k="ETA" v={j.cleaner_eta_minutes != null ? `~${j.cleaner_eta_minutes} min` : undefined} />
                  <KV k="Distance" v={j.cleaner_distance_km != null ? `${j.cleaner_distance_km} km` : undefined} />
                </div>
              </Section>
            )}

            {/* Timeline */}
            <Section title="Timeline" testid="booking-section-timeline">
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                <KV k="Created"    v={j.created_at    ? new Date(j.created_at).toLocaleString()    : undefined} />
                <KV k="Scheduled"  v={j.scheduled_at  ? new Date(j.scheduled_at).toLocaleString()  : undefined} />
                <KV k="Started"    v={j.milestone_started_at   ? new Date(j.milestone_started_at).toLocaleString()   : undefined} />
                <KV k="Completed"  v={j.milestone_completed_at ? new Date(j.milestone_completed_at).toLocaleString() : undefined} />
                <KV k="Paid at"    v={j.paid_at       ? new Date(j.paid_at).toLocaleString()       : undefined} />
              </div>
            </Section>

            {/* Photos */}
            {(j.before_photos?.length || j.after_photos?.length) ? (
              <Section title="Before / after photos" testid="booking-section-photos">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] font-mono uppercase text-slate-400 mb-1"><Camera className="w-3 h-3 inline" /> Before ({(j.before_photos || []).length})</div>
                    <div className="flex flex-wrap gap-2">
                      {(j.before_photos || []).slice(0, 8).map((src, i) => (
                        <img key={i} src={src} alt="" className="w-20 h-20 object-cover rounded border border-white/10" />
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono uppercase text-slate-400 mb-1"><Camera className="w-3 h-3 inline" /> After ({(j.after_photos || []).length})</div>
                    <div className="flex flex-wrap gap-2">
                      {(j.after_photos || []).slice(0, 8).map((src, i) => (
                        <img key={i} src={src} alt="" className="w-20 h-20 object-cover rounded border border-white/10" />
                      ))}
                    </div>
                  </div>
                </div>
              </Section>
            ) : null}

            {/* Chat */}
            {(data.chat || []).length > 0 && (
              <Section title="Messages" testid="booking-section-chat">
                <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 max-h-64 overflow-y-auto text-xs space-y-1">
                  {data.chat.slice(-40).map((m, i) => (
                    <div key={i} className="border-b border-white/5 py-1">
                      <div className="text-[10px] font-mono text-slate-400">{m.sender_email || m.sender_id?.slice(0, 8)} · {m.created_at ? new Date(m.created_at).toLocaleString() : ""}</div>
                      <div className="text-slate-200 whitespace-pre-wrap">{m.text || m.body || m.message || ""}</div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Complaint */}
            {data.complaint && (
              <Section title="Complaint / dispute" testid="booking-section-complaint">
                <div className="rounded-xl border border-amber-400/40 bg-amber-500/[0.06] p-4 text-sm">
                  <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-300" /><Chip tone="amber">{data.complaint.status}</Chip><span className="font-mono text-[10px] text-slate-400">{data.complaint.issue_type}</span></div>
                  <div className="mt-2 text-slate-200 whitespace-pre-wrap">{data.complaint.description}</div>
                  {data.complaint.admin_notes?.length > 0 && (
                    <div className="mt-3 text-xs">
                      <div className="text-slate-400 font-mono uppercase tracking-widest text-[10px] mb-1">Admin determinations</div>
                      {data.complaint.admin_notes.map((n, i) => (
                        <div key={i} className="border-l-2 border-amber-400/40 pl-2 py-1 text-slate-300">
                          <div className="text-[10px] text-slate-400">{n.at ? new Date(n.at).toLocaleString() : ""} · {n.actor_email}</div>
                          {n.note}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Section>
            )}
          </div>
        )}
      </aside>
    </>
  );
}
